import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import type {
  UploadRequest,
  UploadRequestFile,
} from "@/services/types/uploads";
import { UploadService } from "@/services/upload-service";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  CircleX,
  FileSpreadsheet,
  Loader2,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_main/orders/upload-summary/$requestId")(
  {
    component: RouteComponent,
    loader: ({ params }) => {
      return params.requestId;
    },
  },
);

function RouteComponent() {
  const { requestId } = Route.useParams();
  const queryClient = useQueryClient();
  const router = useRouter();
  const [itemTotalCount, setItemTotalCount] = useState<Map<string, number>>(
    new Map(),
  );
  const [files, setFiles] = useState<UploadRequestFile[]>([]);
  const [fileParsed, setFileParsed] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState("");

  const { data: parsedData } = useQuery<UploadRequest | null>({
    queryKey: [`parsedData${requestId}`],
    queryFn: () => UploadService.getUploadRequest(requestId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "pending" || status === "processing" ? 3000 : false;
    },
  });

  const prepAddRequest = (fileId: string) => {
    for (const data of files) {
      if (data.id === fileId) {
        addNewOrderMutation.mutate(data);
      }
    }
  };

  const addNewOrderMutation = useMutation({
    mutationFn: async (data: UploadRequestFile) => {
      const orderPayload = data.llm_response
        ? JSON.parse(JSON.stringify(data.llm_response))
        : undefined;

      const response = await UploadService.confirmFile(requestId, data.id, {
        order: orderPayload,
      });
      return {
        ...data,
        order_id: response.order_id,
        supplier_order_number: response.supplier_order_number ?? null,
      };
    },
    onSuccess: async (confirmedFile) => {
      toast.success("Order added successfully");
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === confirmedFile.id ? confirmedFile : file,
        ),
      );
      await queryClient.invalidateQueries({
        queryKey: [`parsedData${requestId}`],
      });
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await router.invalidate();
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message.trim() !== ""
          ? error.message
          : "Failed to add order";
      toast.error(message);
    },
  });

  useEffect(() => {
    if (!parsedData || fileParsed) {
      return;
    }

    if (parsedData.status !== "completed") {
      return;
    }

    const totals = new Map<string, number>();
    parsedData.files.map((file) => {
      file?.llm_response?.items_ordered?.map((item) => {
        const rawQuantity =
          typeof item.quantity === "string"
            ? Number.parseFloat(item.quantity)
            : Number(item.quantity ?? 0);
        const quantity = Number.isFinite(rawQuantity) ? rawQuantity : 0;
        const name = item.name ?? "Item";
        totals.set(name, (totals.get(name) ?? 0) + quantity);
      });
    });
    setFiles(parsedData.files);
    setItemTotalCount(totals);
    setFileParsed(true);
  }, [parsedData, fileParsed]);

  const deleteOrderItem = (id: string, item_name: string) => {
    const new_files = files.map((f) => {
      if (f.id === id && f.llm_response?.items_ordered) {
        const new_items = f.llm_response.items_ordered.filter(
          (i) => i.name !== item_name,
        );
        return {
          ...f,
          llm_response: {
            ...f.llm_response,
            items_ordered: new_items,
          },
        };
      }
      return f;
    });
    setFiles(new_files);
  };

  const getDisplayOrderNumber = (file: UploadRequestFile) => {
    if (
      file.supplier_order_number !== null &&
      file.supplier_order_number !== undefined &&
      file.supplier_order_number !== ""
    ) {
      return String(file.supplier_order_number);
    }
    return file.order_id ?? "";
  };

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-10 lg:py-12">
      <Link
        to="/orders/upload"
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Upload
      </Link>

      <header className="space-y-4">
        <div className="space-y-2">
          <h1 className="font-semibold text-4xl">Upload Orders</h1>
          <p className="text-base text-muted-foreground">
            Review and confirm your uploaded orders
          </p>
        </div>
      </header>

      <section className="space-y-8">
        <ol className="flex flex-wrap items-center gap-6 text-sm">
          <li className="flex items-center gap-3">
            <span className="flex size-11 items-center justify-center rounded-full bg-brand-yellowGreen text-brand-darkGreen">
              <CheckCircle2 className="size-5" />
            </span>
            <div>
              <p className="font-semibold text-brand-darkGreen text-sm">
                Upload
              </p>
              <p className="text-muted-foreground text-xs">Step 01</p>
            </div>
          </li>
          <li className="flex items-center gap-3">
            <span className="flex size-11 items-center justify-center rounded-full bg-brand-darkGreen font-semibold text-base text-white">
              02
            </span>
            <div>
              <p className="font-semibold text-sm">Summary</p>
              <p className="text-muted-foreground text-xs">
                Review &amp; confirm
              </p>
            </div>
          </li>
        </ol>

        <div className="grid gap-8 lg:grid-cols-[minmax(0,2fr)_minmax(340px,1fr)]">
          <Card className="rounded-[32px] border-none bg-white shadow-lg shadow-slate-200/60">
            <CardHeader className="gap-4 pb-0">
              <CardTitle className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-brand-darkGreen/80 text-sm uppercase tracking-widest">
                    Items by Category
                  </p>
                  <p className="text-muted-foreground text-sm">
                    Breakdown of all items grouped by category
                  </p>
                </div>
                <Badge className="rounded-full bg-brand-yellowGreen px-2 py-1 font-semibold text-base text-brand-darkGreen hover:bg-brand-yellowGreen">
                  {itemTotalCount.size}
                </Badge>
                <div className="relative">
                  <Search className="-translate-y-1/2 absolute top-1/2 left-4 size-4 text-muted-foreground" />
                  <Input
                    placeholder="Search"
                    className="h-12 rounded-full border-slate-200 bg-slate-50 pl-12 text-sm"
                  />
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="overflow-hidden rounded-[28px] border border-slate-100">
                <div className="grid grid-cols-[2fr_1fr] items-center bg-slate-50 px-6 py-4 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                  <span>Category</span>
                  <span className="text-right">Total</span>
                </div>
                {!fileParsed ? (
                  <div className="grid h-screen place-items-center">
                    <Loader2 className="animate-spin" />
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100 bg-white">
                    {Array.from(itemTotalCount.entries()).map(
                      ([key, value]) => (
                        <div
                          key={key}
                          className="grid grid-cols-[2fr_1fr] items-center px-6 py-5"
                        >
                          <span className="font-medium text-base text-foreground">
                            {key}
                          </span>
                          <span className="text-right font-semibold text-lg">
                            {value}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-[32px] border border-amber-200 bg-amber-50 px-1 py-6 shadow-none">
            <CardHeader className="gap-3 pb-0">
              <div className="flex items-center gap-2 text-amber-900">
                <AlertTriangle className="size-5" />
                <div className="font-semibold text-base">
                  Unconfirmed Orders (0)
                </div>
              </div>
              <p className="text-amber-900/80 text-sm">
                These orders contain items that need review or correction before
                importing.
              </p>
            </CardHeader>
            {!fileParsed ? (
              <div className="grid h-screen place-items-center">
                <Loader2 className="animate-spin" />
              </div>
            ) : (
              <CardContent className="space-y-4">
                {files.map((t) => (
                  <div
                    key={t.id}
                    className="rounded-3xl border border-amber-200 bg-white/95 p-5 shadow-amber-100 shadow-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex flex-col">
                        <div className="flex items-center gap-3">
                          <span className="flex size-10 items-center justify-center rounded-full bg-brand-yellowGreen text-brand-darkGreen">
                            <FileSpreadsheet className="size-5" />
                          </span>
                          <div>
                            <p className="font-semibold text-sm">
                              {t.original_name}
                            </p>
                          </div>
                        </div>
                      </div>
                      {t.order_id === "" ? (
                        <Button
                          variant="default"
                          size="sm"
                          className="rounded-full px-5"
                          disabled={addNewOrderMutation.isPending}
                          onClick={() => {
                            prepAddRequest(t.id);
                          }}
                        >
                          {addNewOrderMutation.isPending
                            ? "Confirming..."
                            : "Confirm"}
                        </Button>
                      ) : (
                        <div className="rounded-md bg-green-100 px-2 py-0.5 text-center text-green-800 text-xs">
                          <div>Order ID</div>
                          <span className="text-sm">{getDisplayOrderNumber(t)}</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                          Unrecognized Items
                        </p>
                      </div>

                      <div>
                        <p className="font-semibold text-muted-foreground text-xs uppercase tracking-wide">
                          Confirmed Items
                        </p>
                        <div className="mt-2 space-y-2">
                          {t?.llm_response?.items_ordered?.map((item) => (
                            <div
                              key={item.name}
                              className="flex items-center justify-between rounded-2xl border border-slate-100 px-2 py-2 text-sm"
                            >
                              <Dialog open={deleteConfirmName !== ""}>
                                <DialogTrigger asChild>
                                  <CircleX
                                    className="m-0 size-6 p-0 pr-2"
                                    style={{ color: "#ff0000" }}
                                    onClick={() =>
                                      setDeleteConfirmName(item.name)
                                    }
                                  />
                                </DialogTrigger>
                                <DialogContent className="sm:max-w-[425px]">
                                  <DialogHeader>
                                    <DialogTitle>Remove item?</DialogTitle>
                                    <DialogDescription>
                                      <p>
                                        Are you sure you want to remove "
                                        {deleteConfirmName}"?
                                      </p>
                                      <p>
                                        This action cannot be undone and you
                                        will have to upload this file again to
                                        retrieve all items.
                                      </p>
                                    </DialogDescription>
                                  </DialogHeader>

                                  <div className="flex justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => setDeleteConfirmName("")}
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={() => {
                                        deleteOrderItem(t.id, item.name);
                                        setDeleteConfirmName("");
                                      }}
                                    >
                                      Delete
                                    </Button>
                                  </div>
                                </DialogContent>
                              </Dialog>
                              <span>{item.name}</span>
                              <Badge
                                variant="outline"
                                className="rounded-full border-slate-200 text-xs"
                              >
                                {item.quantity}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            )}
          </Card>
        </div>
      </section>
    </div>
  );
}

export default RouteComponent;
